/**
 * Public developer-API application form.
 *
 * Lives at `/developer/apply` — anonymous, no auth, anyone can submit.
 * Hits `POST /api/developers/apply` and shows a confirmation screen
 * with the next steps. The SuperAdmin gets an email at the configured
 * approver address; the applicant gets a confirmation email at the
 * address they entered.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Coffee, KeyRound, Check, Mail, FileText } from "lucide-react";

export default function DeveloperApply() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", website: "", contact_phone: "", intended_use: "",
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.intended_use.trim().length < 10) {
      toast.error("Name, email, and a description of intended use are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/developers/apply", {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        website: form.website.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
        intended_use: form.intended_use.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1A30] flex items-start sm:items-center justify-center p-4 sm:p-8" data-testid="developer-apply-page">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C9A84C]/20 text-[#C9A84C] text-xs uppercase tracking-widest font-semibold mb-3">
            <Coffee className="h-3.5 w-3.5" /> Developer programme
          </div>
          <h1 className="text-white font-heading text-3xl sm:text-4xl font-bold">Request API access</h1>
          <p className="text-gray-300 text-sm mt-2 max-w-lg mx-auto">
            Build integrations on top of Coffee Lovers Café — payroll connectors, BI dashboards,
            timesheet aggregators. Submit the form below and our team will review your request.
          </p>
          <div className="mt-4">
            <Button
              variant="outline"
              asChild
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white"
              data-testid="apply-download-guide"
            >
              <a href="/api/developer-guide.pdf" target="_blank" rel="noreferrer">
                <FileText className="h-3.5 w-3.5 mr-2" /> Read the integration guide (PDF)
              </a>
            </Button>
          </div>
        </div>

        {!submitted && (
          <Card>
            <CardContent className="p-6 sm:p-8">
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="apply-name">Your name *</Label>
                    <Input id="apply-name" data-testid="apply-name" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Smith" />
                  </div>
                  <div>
                    <Label htmlFor="apply-email">Email *</Label>
                    <Input id="apply-email" type="email" data-testid="apply-email" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@example.com" />
                  </div>
                  <div>
                    <Label htmlFor="apply-company">Company</Label>
                    <Input id="apply-company" data-testid="apply-company" value={form.company}
                      onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Acme Pty Ltd" />
                  </div>
                  <div>
                    <Label htmlFor="apply-website">Website</Label>
                    <Input id="apply-website" data-testid="apply-website" value={form.website}
                      onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://acme.com" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="apply-phone">Contact phone</Label>
                    <Input id="apply-phone" data-testid="apply-phone" value={form.contact_phone}
                      onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+64 ..." />
                  </div>
                </div>
                <div>
                  <Label htmlFor="apply-use">How will you use the API? *</Label>
                  <Textarea
                    id="apply-use"
                    data-testid="apply-intended-use"
                    rows={5}
                    value={form.intended_use}
                    onChange={(e) => setForm({ ...form, intended_use: e.target.value })}
                    placeholder="Describe the integration you're building, the endpoints you'll need, and roughly the request volume."
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum 10 characters. Be specific — it helps the review.</p>
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 text-base bg-[#1A2744] hover:bg-[#263A63] text-white font-semibold"
                  disabled={busy}
                  data-testid="apply-submit"
                >
                  {busy ? "Sending…" : "Submit application"}
                </Button>
                <p className="text-[11px] text-gray-400 text-center">
                  By submitting, you agree to the Coffee Lovers Café API terms. Approved partners
                  receive a single API key by email — please store it somewhere safe.
                </p>
              </form>
            </CardContent>
          </Card>
        )}

        {submitted && (
          <Card data-testid="apply-success">
            <CardContent className="p-8 text-center space-y-4">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 text-emerald-700">
                <Check className="h-7 w-7" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-[#1A2744]">Application received</h2>
              <p className="text-sm text-gray-600 max-w-md mx-auto">
                Thanks, <strong>{form.name}</strong>. We&apos;ve emailed a confirmation to <strong>{form.email}</strong>{" "}
                and notified our review team. Once approved, your API key will arrive by email.
              </p>
              <div className="bg-[#FCF8EF] border border-[#C9A84C]/40 rounded-lg p-4 text-xs text-left text-gray-600 max-w-md mx-auto">
                <div className="flex items-center gap-2 text-[#1A2744] font-semibold mb-1">
                  <KeyRound className="h-3.5 w-3.5" /> What happens next
                </div>
                <ol className="list-decimal pl-4 space-y-1">
                  <li>Our team reviews your intended use.</li>
                  <li>If approved, we email a single API key to <strong>{form.email}</strong>.</li>
                  <li>You use the key as a <code>Bearer</code> token on each request.</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
